import { ChevronRight } from 'lucide-react'
import RulesInfoButton from '../../features/rules/RulesInfoButton'
import { sharpestWeekChipLine } from './records'
import type { BestGameweekRecord } from './records'
import './ProfileRecords.css'

/**
 * The sharpest week row on the profile, lifted out of `ProfileRecords` so the rules
 * demo can render the shipping card rather than a drawing of it.
 */
export default function SharpestWeekCard({
  record,
  walk,
  onOpenGameweek,
}: {
  record: BestGameweekRecord | null
  /** Every gameweek the modal's arrows should step through, when this row opens into a walk
   *  rather than a single week. Handed down rather than derived here: the card knows one record,
   *  not whose season it belongs to. */
  walk?: number[]
  onOpenGameweek?: (gameweek: number, walk?: number[]) => void
}) {
  // The card is a div with a stretched button inside it rather than one big button, so the rules
  // ⓘ can sit beside the label without nesting a button in a button. The hit target
  // is positioned, so it still takes every tap on the text below it — the whole box behaves
  // exactly as it did when it was the button.

  // Before any gameweek has settled there is nothing to rank — same treatment as an unplayed
  // chip tile (dimmed, inert, still in place) rather than dropping the row.
  if (record === null) {
    return (
      <div className="profile-records__row">
        <div className="profile-records__best profile-records__best--empty">
          <button
            type="button"
            className="profile-records__best-hit"
            aria-label="Sharpest week, no settled gameweek yet"
            disabled
          />

          <span className="profile-records__best-head">
            <SharpestWeekLabel />
          </span>

          <span className="profile-records__best-value" aria-hidden="true">
            -
          </span>

          {/* The dash alone reads as a data bug; this line says it's waiting, not broken. */}
          <span className="profile-records__best-meta" aria-hidden="true">
            No settled gameweek yet
          </span>
        </div>
      </div>
    )
  }

  const chipLine = sharpestWeekChipLine(record)

  return (
    <div className="profile-records__row">
      <div className="profile-records__best">
        {/* Named explicitly because every word on the card is now outside this button. The visual
            spans are hidden from the a11y tree in turn, so the row is read once, not twice — same
            idiom as the chip tile below. The marks keep their own description. */}
        <button
          type="button"
          className="profile-records__best-hit"
          disabled={!onOpenGameweek}
          aria-label={`Sharpest week, gameweek ${record.gameweek}, ${record.points} points${
            chipLine === null ? '' : `, ${chipLine}`
          }. View gameweek results.`}
          onClick={() => onOpenGameweek?.(record.gameweek, walk)}
        />

        <span className="profile-records__best-head">
          <SharpestWeekLabel />
          <span className="profile-records__best-ref" aria-hidden="true">
            GW{record.gameweek}
            <ChevronRight aria-hidden="true" />
          </span>
        </span>

        {/* The week as it was actually scored, chips included. It is *chosen* on chip-free base
            points, which is why a chip week can show a bigger number here than the football alone
            earned — the ⓘ beside the label is where that is explained, not a second numeral. */}
        <span className="profile-records__best-value" aria-hidden="true">
          {record.points}
          <span className="profile-records__best-unit">pts</span>
          {chipLine !== null && (
            <span className="profile-records__best-chip label-eyebrow">{chipLine}</span>
          )}
        </span>

        {/* "correct", never "called" — a call is a prediction in this app. An
            exact score is also a correct outcome, so the two figures nest rather than add. */}
        {/* Partials are in the line because they are in the mark row: an amber square with no
            number beside it makes the text look like it lost count. Counting the marks now
            matches reading the text. */}
        <span className="profile-records__best-meta" aria-hidden="true">
          {record.correctOutcomes} correct · {record.partialScores} partial ·{' '}
          {record.exactScores} exact
        </span>

        {record.marks.length > 0 && (
          <span
            className="profile-records__marks"
            role="img"
            aria-label={`${record.correctOutcomes} correct, ${record.partialScores} partial and ${record.exactScores} exact from ${record.marks.length} fixtures`}
          >
            {record.marks.map((tier, index) => (
              <span
                key={index}
                className={`profile-records__mark profile-records__mark--${tier}`}
              />
            ))}
          </span>
        )}
      </div>
    </div>
  )
}

/** The eyebrow and its ⓘ, grouped so `space-between` in the head row keeps the mark tight against
 *  the label instead of flinging it at the GW reference on the far side.
 *
 *  "Sharpest week", not "best": the row is ranked on chip-free base points, so a Double Up tile
 *  below can legitimately show a bigger number. "Best" invited the reader to call that a bug.
 *  The ⓘ is where the rest of that explanation lives. */
function SharpestWeekLabel() {
  return (
    <span className="profile-records__best-head-label">
      <span className="profile-records__label label-eyebrow" aria-hidden="true">
        Sharpest week
      </span>
      <RulesInfoButton section="sharpest" className="profile-records__best-info" />
    </span>
  )
}
